import React, { useState } from "react";
import { formatDate } from "./blogFirebase";

// ── SEO Intelligence Panel ────────────────────────────────────────────────────
const SeoIntelligencePanel = ({ post }) => {
  const [showSourceData, setShowSourceData] = useState(false);

  const scQueries = post.aiContext?.searchConsoleTopQueries || [];
  const ga4Pages  = post.aiContext?.ga4TopPages || [];
  const hasSource = scQueries.length > 0 || ga4Pages.length > 0;

  return (
    <div className="mb-8 rounded-xl border border-emerald-200 bg-emerald-50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-emerald-100 border-b border-emerald-200">
        <span className="text-base">🎯</span>
        <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">SEO Intelligence</span>
        <span className="ml-auto text-[10px] text-emerald-600 font-medium">AI keyword targeting</span>
      </div>

      <div className="px-4 py-3 space-y-3">
        {/* Targeted keywords */}
        {post.targetedKeywords?.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide mb-1.5">Targeted keywords</p>
            <div className="flex flex-wrap gap-1.5">
              {post.targetedKeywords.map((kw, i) => (
                <span
                  key={i}
                  className="px-2.5 py-0.5 bg-white border border-emerald-300 text-emerald-800 text-xs rounded-full font-medium shadow-sm"
                >
                  {kw}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Rationale */}
        {post.keywordRationale && (
          <div>
            <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide mb-1">Why the AI chose these</p>
            <p className="text-xs text-emerald-900 leading-relaxed">{post.keywordRationale}</p>
          </div>
        )}

        {/* Collapsible source data */}
        {hasSource && (
          <div>
            <button
              onClick={() => setShowSourceData(v => !v)}
              className="text-[10px] font-semibold text-emerald-600 hover:text-emerald-800 underline underline-offset-2 transition-colors"
            >
              {showSourceData ? "▲ Hide source data" : "▼ Show source data (SC + GA4)"}
            </button>

            {showSourceData && (
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Search Console */}
                {scQueries.length > 0 && (
                  <div className="bg-white rounded-lg border border-emerald-200 p-3">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      🔍 Search Console queries
                    </p>
                    <ul className="space-y-1">
                      {scQueries.slice(0, 10).map((q, i) => (
                        <li key={i} className="flex items-baseline justify-between gap-2 text-[11px]">
                          <span className="text-gray-700 truncate">"{q.query}"</span>
                          <span className="text-gray-400 whitespace-nowrap shrink-0">
                            {q.impressions?.toLocaleString()} imp · pos {Number(q.position).toFixed(1)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* GA4 */}
                {ga4Pages.length > 0 && (
                  <div className="bg-white rounded-lg border border-emerald-200 p-3">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      📈 GA4 top pages
                    </p>
                    <ul className="space-y-1">
                      {ga4Pages.slice(0, 10).map((p, i) => (
                        <li key={i} className="flex items-baseline justify-between gap-2 text-[11px]">
                          <span className="text-gray-700 truncate">{p.page}</span>
                          <span className="text-gray-400 whitespace-nowrap shrink-0">
                            {p.sessions?.toLocaleString()} sessions
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Hero image, article header, rendered HTML body, and secondary (mid-article) image.
 * onOpenMainImgModal / onOpenSecondaryImgModal — optional callbacks to open the image edit modal.
 * When not provided (e.g. published post view), the edit button is hidden.
 */
const BlogPostBody = ({
  post,
  contentHtml,
  showImgPrompt,
  onToggleImgPrompt,
  showSecondaryImgPrompt,
  onToggleSecondaryImgPrompt,
  onOpenMainImgModal,
  onOpenSecondaryImgModal,
}) => (
  <div className="max-w-[860px] mx-auto px-4 py-8">
    {/* Hero + Mid-article images — stacked on mobile, side by side on sm+ */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
      {/* Hero image */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Hero image</p>
        {post.image ? (
          <div className="relative mb-1">
            <img
              src={post.image}
              alt={post.title}
              className="w-full rounded-xl object-cover"
              style={{ maxHeight: "280px" }}
            />
            {/* Edit button — top left */}
            {onOpenMainImgModal && (
              <button
                onClick={onOpenMainImgModal}
                className="absolute top-2 left-2 px-2 py-1 bg-black/50 text-white text-xs rounded-lg hover:bg-black/70 font-medium"
              >
                ✏️ Edit
              </button>
            )}
            {post.imagePrompt && (
              <button
                onClick={onToggleImgPrompt}
                className="absolute bottom-3 right-3 px-2 py-0.5 bg-black/50 text-white text-xs rounded-full hover:bg-black/70"
              >
                {showImgPrompt ? "✕ Hide prompt" : "🔍 Image prompt"}
              </button>
            )}
          </div>
        ) : (
          <div className="relative w-full h-40 bg-gray-100 rounded-xl flex items-center justify-center mb-1">
            <span className="text-5xl">{post.emoji || "🎂"}</span>
            {onOpenMainImgModal && (
              <button
                onClick={onOpenMainImgModal}
                className="absolute top-2 left-2 px-2 py-1 bg-black/50 text-white text-xs rounded-lg hover:bg-black/70 font-medium"
              >
                ✏️ Edit
              </button>
            )}
          </div>
        )}
        {showImgPrompt && post.imagePrompt && (
          <div className="mb-2 px-3 py-2 bg-gray-900 text-gray-200 text-xs leading-relaxed font-mono rounded-b-xl border-t border-gray-700 break-words whitespace-pre-wrap">
            <span className="text-gray-500 uppercase tracking-wide text-[10px] block mb-1">Image generation prompt</span>
            {post.imagePrompt}
          </div>
        )}
      </div>

      {/* Mid-article image */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Mid-article image</p>
        {post.secondaryImage ? (
          <div className="relative mb-1">
            <img
              src={post.secondaryImage}
              alt={`${post.title} — secondary`}
              className="w-full rounded-xl object-cover"
              style={{ maxHeight: "280px" }}
            />
            {/* Edit button — top left */}
            {onOpenSecondaryImgModal && (
              <button
                onClick={onOpenSecondaryImgModal}
                className="absolute top-2 left-2 px-2 py-1 bg-black/50 text-white text-xs rounded-lg hover:bg-black/70 font-medium"
              >
                ✏️ Edit
              </button>
            )}
            {post.secondaryImagePrompt && (
              <button
                onClick={onToggleSecondaryImgPrompt}
                className="absolute bottom-3 right-3 px-2 py-0.5 bg-black/50 text-white text-xs rounded-full hover:bg-black/70"
              >
                {showSecondaryImgPrompt ? "✕ Hide prompt" : "🔍 Image prompt"}
              </button>
            )}
          </div>
        ) : (
          <div className="relative w-full h-40 bg-gray-100 rounded-xl flex items-center justify-center mb-1">
            <span className="text-gray-400 text-sm">No mid-article image yet</span>
            {onOpenSecondaryImgModal && (
              <button
                onClick={onOpenSecondaryImgModal}
                className="absolute top-2 left-2 px-2 py-1 bg-black/50 text-white text-xs rounded-lg hover:bg-black/70 font-medium"
              >
                ✏️ Edit
              </button>
            )}
          </div>
        )}
        {showSecondaryImgPrompt && post.secondaryImagePrompt && (
          <div className="mb-2 px-3 py-2 bg-gray-900 text-gray-200 text-xs leading-relaxed font-mono rounded-b-xl border-t border-gray-700 break-words whitespace-pre-wrap">
            <span className="text-gray-500 uppercase tracking-wide text-[10px] block mb-1">Secondary image generation prompt</span>
            {post.secondaryImagePrompt}
          </div>
        )}
      </div>
    </div>

    {/* Article header */}
    <div className="max-w-[740px] mx-auto mt-6 mb-6">
      <div className="flex flex-wrap gap-2 mb-3">
        {(post.tags || []).slice(0, 3).map(tag => (
          <span key={tag} className="px-3 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">{tag}</span>
        ))}
        {post.writingTheme && (
          <span className="px-3 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
            ✍️ {post.writingTheme.split(' —')[0]}
          </span>
        )}
      </div>

      <h1 style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "1.875rem", lineHeight: 1.25, color: "#1a1a2e", marginBottom: "0.75rem" }}>
        {post.emoji && <span className="mr-2">{post.emoji}</span>}{post.title}
      </h1>

      <p style={{ color: "#555", fontSize: "1.0625rem", marginBottom: "0.75rem", lineHeight: 1.6 }}>
        {post.description}
      </p>

      <div className="flex gap-4 text-sm text-gray-400 mb-8">
        <span>{formatDate(post.date)}</span>
        <span>·</span>
        <span>{post.readTime || "5 min read"}</span>
        <span>·</span>
        <span className="text-gray-300">/{post.slug}</span>
      </div>

      {/* SEO Intelligence panel — only shown when AI keyword data is available */}
      {(post.targetedKeywords?.length > 0 || post.keywordRationale) && (
        <SeoIntelligencePanel post={post} />
      )}

      <hr style={{ borderTop: "1px solid #e5e7eb", marginBottom: "2rem" }} />

      {/* Rendered HTML body */}
      <div
        className="prose-blog"
        dangerouslySetInnerHTML={{ __html: contentHtml }}
      />
    </div>

  </div>
);

export default BlogPostBody;
